import React, { useEffect } from "react";
import DeviceList from "./DeviceList";
import AlertPopover from "./AlertPopover";

const YotoLogin = () => {
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = React.useState(false);
  const [showInstallButton, setShowInstallButton] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showAlert, setShowAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  };

  // Check if already running as an installed app
  const isInstalled = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone ||
      document.referrer.includes("android-app://")
    );
  };

  useEffect(() => {
    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (isMobile()) {
        setShowInstallButton(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check if we're handling an OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("code")) {
      handleOAuthCallback();
    } else {
      checkLoginStatus();
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const checkLoginStatus = async () => {
    try {
      const response = await fetch("/auth/status", {
        credentials: "include",
      });

      const loggedIn = response.ok;
      setIsLoggedIn(loggedIn);
    } catch (error) {
      console.error("Failed to check login status:", error);
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const sessionId = localStorage.getItem("oauth_session_id");

      if (!code || !state || !sessionId) {
        throw new Error("Missing OAuth parameters");
      }

      const response = await fetch("/auth/callback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          session_id: sessionId,
          code: code,
          state: state,
        }),
      });

      if (response.ok) {
        setIsLoggedIn(true);
        localStorage.removeItem("oauth_session_id");
        window.history.replaceState({}, document.title, "/");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Login failed");
      }
    } catch (error) {
      console.error("Login failed:", error);
      setIsLoggedIn(false);
      setAlertMessage(`Login failed: ${error}`);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    // Check if we should show install prompt first
    if (
      isMobile() &&
      !isInstalled() &&
      deferredPrompt &&
      !localStorage.getItem("install-prompt-shown")
    ) {
      setShowInstallPrompt(true);
      localStorage.setItem("install-prompt-shown", "true");
      return;
    }

    // Proceed with OAuth
    proceedWithLogin();
  };

  const handleContinueAnyway = () => {
    setShowInstallPrompt(false);
    proceedWithLogin();
  };

  const proceedWithLogin = async () => {
    try {
      const response = await fetch("/auth/login");
      const data = await response.json();

      localStorage.setItem("oauth_session_id", data.session_id);
      window.location.href = data.authorization_url;
    } catch (error) {
      console.error("Login initiation failed:", error);
      setAlertMessage(`Failed to start login process: ${error}`);
      setShowAlert(true);
    }
  };

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`Install prompt outcome: ${outcome}`);
      setDeferredPrompt(null);
    }
    setShowInstallPrompt(false);
  };

  const handleLogout = async () => {
    try {
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout request failed:", error);
    } finally {
      localStorage.removeItem("oauth_session_id");
      setIsLoggedIn(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              marginBottom: "20px",
              animation: "pulse 2s infinite",
            }}
          >
            ⚡
          </div>
          <h2
            style={{
              margin: "0 0 10px 0",
              fontSize: "24px",
              color: "#333",
              fontWeight: "normal",
            }}
          >
            Loading Charge My Yoto!...
          </h2>
          <p
            style={{
              margin: "0",
              color: "#666",
              fontSize: "16px",
            }}
          >
            Please wait
          </p>
        </div>
        <style jsx>{`
          @keyframes pulse {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
            }
          }
        `}</style>
      </div>
    );
  }

  if (isLoggedIn) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
        {/* Header with logout */}
        <header
          style={{
            backgroundColor: "white",
            borderBottom: "1px solid #e9ecef",
            padding: "20px 0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              maxWidth: "600px",
              margin: "0 auto",
              padding: "0 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span style={{ fontSize: "24px" }}>⚡</span>
              <div>
                <h1
                  style={{
                    margin: "0",
                    fontSize: isMobile() ? "16px" : "24px",
                    color: "#333",
                    fontWeight: "bold",
                  }}
                >
                  Charge My Yoto!
                </h1>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              {showInstallButton && (
                <button
                  onClick={handleInstallApp}
                  style={{
                    padding: "8px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    transition: "background-color 0.2s",
                    boxShadow: "0 2px 4px rgba(220, 53, 69, 0.2)",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#0056b3";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#007bff";
                  }}
                >
                  Install
                </button>
              )}

              <button
                onClick={handleLogout}
                style={{
                  padding: "8px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  transition: "background-color 0.2s",
                  boxShadow: "0 2px 4px rgba(220, 53, 69, 0.2)",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#c82333";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#dc3545";
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            padding: "20px",
          }}
        >
          <DeviceList />
        </main>
      </div>
    );
  }

  // Install prompt overlay
  if (showInstallPrompt) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          padding: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "40px",
            maxWidth: "400px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
            animation: "slideUp 0.3s ease-out",
          }}
        >
          <style jsx>{`
            @keyframes slideUp {
              from {
                transform: translateY(50px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>

          <div style={{ fontSize: "64px", marginBottom: "20px" }}>📱</div>

          <h2
            style={{
              margin: "0 0 15px 0",
              fontSize: "24px",
              color: "#333",
              fontWeight: "bold",
            }}
          >
            We recommend installing!
          </h2>

          <p
            style={{
              margin: "0 0 30px 0",
              fontSize: "16px",
              color: "#666",
              lineHeight: 1.5,
            }}
          >
            Some mobile browsers may block notifications if{" "}
            <i>Charge My Yoto!</i> is not installed. Sorry!
          </p>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <button
              onClick={handleInstallApp}
              style={{
                padding: "16px 24px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
                transition: "all 0.2s",
                boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#0056b3";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "#007bff";
                e.target.style.transform = "translateY(0)";
              }}
            >
              Install as App
            </button>

            <button
              onClick={handleContinueAnyway}
              style={{
                padding: "16px 24px",
                backgroundColor: "transparent",
                color: "#666",
                border: "2px solid #e9ecef",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "normal",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#f8f9fa";
                e.target.style.borderColor = "#dee2e6";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.borderColor = "#e9ecef";
              }}
            >
              Continue in Browser
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        padding: "20px",
      }}
    >
      <div
        style={{
          textAlign: "center",
          padding: "60px 40px",
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          border: "1px solid #e9ecef",
          maxWidth: "500px",
          width: "100%",
        }}
      >
        <div
          style={{
            fontSize: "64px",
            marginBottom: "30px",
          }}
        >
          ⚡
        </div>

        <h1
          style={{
            margin: "0 0 15px 0",
            fontSize: "32px",
            color: "#333",
            fontWeight: "bold",
          }}
        >
          Charge My Yoto!
        </h1>

        <p
          style={{
            margin: "0 0 40px 0",
            fontSize: "18px",
            color: "#666",
            lineHeight: 1.5,
          }}
        >
          Monitor your Yoto players and get notified when their batteries are
          running low.
        </p>

        <button
          onClick={handleLogin}
          style={{
            padding: "16px 32px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            fontSize: "18px",
            fontWeight: "bold",
            transition: "all 0.2s",
            boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            margin: "0 auto",
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = "#0056b3";
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 16px rgba(0, 123, 255, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = "#007bff";
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(0, 123, 255, 0.3)";
          }}
        >
          <span style={{ fontSize: "20px" }}>🔑</span>
          Sign in with Yoto
        </button>

        <div
          style={{
            marginTop: "30px",
            padding: "20px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#666",
            textAlign: "left",
          }}
        >
          <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
            What you'll get:
          </h4>
          <ul style={{ margin: "0", paddingLeft: "20px" }}>
            <li>🔋 Real-time battery level monitoring</li>
            <li>📱 Push notifications for low battery</li>
            <li>⚙️ Customizable notification thresholds</li>
            <li>🔒 Secure connection to your Yoto account</li>
          </ul>
        </div>
      </div>
      <AlertPopover
        message={alertMessage}
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default YotoLogin;
